import { HELP_ARTICLES } from "@/lib/registrant-help/articles";
import type { HelpArticle, HelpSearchHit } from "@/lib/registrant-help/types";

function flattenArticleText(article: HelpArticle): string {
  const sectionText = article.sections
    .flatMap((section) => [
      section.heading,
      ...section.blocks.flatMap((block) =>
        block.type === "p" ? [block.text] : block.items,
      ),
    ])
    .join(" ");
  return [
    article.title,
    article.summary,
    article.keywords.join(" "),
    sectionText,
  ]
    .join(" ")
    .toLowerCase();
}

const ARTICLE_SEARCH_TEXT = new Map(
  HELP_ARTICLES.map((article) => [article.id, flattenArticleText(article)]),
);

function scoreArticle(article: HelpArticle, terms: string[]): number {
  const haystack = ARTICLE_SEARCH_TEXT.get(article.id) ?? "";
  const title = article.title.toLowerCase();
  const summary = article.summary.toLowerCase();
  const keywords = article.keywords.join(" ").toLowerCase();
  let score = 0;
  for (const term of terms) {
    if (!haystack.includes(term)) {
      return 0;
    }
    if (title.includes(term)) score += 8;
    if (keywords.includes(term)) score += 4;
    if (summary.includes(term)) score += 3;
    score += 1;
  }
  return score;
}

export function searchHelpArticles(query: string): HelpSearchHit[] {
  const terms = query
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter((term) => term.length > 0);
  if (terms.length === 0) {
    return [];
  }
  return HELP_ARTICLES.map((article) => ({
    article,
    score: scoreArticle(article, terms),
  }))
    .filter((hit) => hit.score > 0)
    .sort(
      (a, b) =>
        b.score - a.score || a.article.title.localeCompare(b.article.title),
    );
}
